import { DeleteOutlined, SaveOutlined } from "@ant-design/icons";
import { App, Button, DatePicker, Input, InputNumber, Modal, Space, Tag } from "antd";
import type { ColumnsType } from "antd/es/table";
import dayjs from "dayjs";
import { useEffect, useMemo, useState } from "react";
import { createDraft, submitDraft, updateDraft, type EntryDraftRecord } from "../../api/draftApi";
import { saveBulk } from "../../api/entryApi";
import { SmartLookupField } from "../lookup/SmartLookupField";
import { PersistedTable } from "../components/PersistedTable";
import { formatInr } from "../utils/currency";
import { UI_DATE_FORMAT, todayInput } from "../utils/date";
import { createSimpleBulkRow, normalizePastedDate, parsePastedNumber, type SimpleBulkRow } from "./simpleBulkEntryUtils";

export function SimpleBulkEntryModal({
  open,
  onClose,
  onSaved,
  moduleKey,
  draft,
  onDraftSaved
}: {
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
  moduleKey: "CashEntry" | "StatementEntry";
  draft?: EntryDraftRecord | null;
  onDraftSaved?: () => void;
}) {
  const { message } = App.useApp();
  const [rows, setRows] = useState<SimpleBulkRow[]>(() => Array.from({ length: 5 }, createSimpleBulkRow));
  const [saving, setSaving] = useState(false);
  const isCash = moduleKey === "CashEntry";
  const title = isCash ? "Cash Entry - Bulk Entry" : "Statement Entry - Bulk Entry";
  const saveUrl = isCash ? "/cash-entries/bulk-upsert" : "/statement-entries/bulk-upsert";

  const totals = useMemo(() => rows.reduce((acc, row) => ({
    debit: acc.debit + Number(row.debit ?? 0),
    credit: acc.credit + Number(row.credit ?? 0)
  }), { debit: 0, credit: 0 }), [rows]);

  const invalidRowKeys = useMemo(() => {
    const keys = new Set<string>();
    rows.forEach((row) => {
      const isActive = !!(row.paidOf || row.paymentOf || row.particulars || row.chequeNo || row.debit || row.credit || row.remarks || row.narration);
      if (!isActive) return;
      const amountMissing = Number(row.debit ?? 0) <= 0 && Number(row.credit ?? 0) <= 0;
      const cashMissing = isCash && (!row.paidOf || !row.paymentOf);
      const statementMissing = !isCash && !row.particulars;
      if (!row.dated || amountMissing || cashMissing || statementMissing) {
        keys.add(row.key);
      }
    });
    return keys;
  }, [isCash, rows]);

  useEffect(() => {
    if (!open) return;
    if (draft?.payload?.rows?.length) {
      setRows(draft.payload.rows);
      return;
    }
    setRows(Array.from({ length: 5 }, createSimpleBulkRow));
  }, [draft, open]);

  const update = (key: string, patch: Partial<SimpleBulkRow>) => {
    setRows((current) => current.map((row) => row.key === key ? { ...row, ...patch } : row));
  };

  const saveDraft = () => {
    if (!isCash) return;
    const title = buildSimpleDraftTitle(rows, "Cash Entry Draft");
    const saveTask = draft
      ? updateDraft(draft.id, { moduleKey: "CashEntry", title, payload: { rows } })
      : createDraft({ moduleKey: "CashEntry", title, payload: { rows } });
    saveTask
      .then(() => {
        message.success("Draft saved");
        onDraftSaved?.();
      })
      .catch(() => message.error("Unable to save draft"));
  };

  const validateRows = () => {
    const activeRows = rows
      .map((row, index) => ({ row, index }))
      .filter(({ row }) => row.paidOf || row.paymentOf || row.particulars || row.chequeNo || row.debit || row.credit || row.remarks || row.narration);
    const errors = activeRows.flatMap(({ row, index }) => {
      const rowNo = index + 1;
      const next: string[] = [];
      if (!row.dated) next.push(`Row ${rowNo}: Date is required`);
      if (isCash) {
        if (!row.paidOf) next.push(`Row ${rowNo}: Paid Of is required`);
        if (!row.paymentOf) next.push(`Row ${rowNo}: Payment Of is required`);
      } else if (!row.particulars) {
        next.push(`Row ${rowNo}: Particulars are required`);
      }
      if (Number(row.debit ?? 0) <= 0 && Number(row.credit ?? 0) <= 0) next.push(`Row ${rowNo}: Debit or Credit is required`);
      return next;
    });
    return { activeRows, errors };
  };

  const save = async () => {
    const { activeRows, errors } = validateRows();
    if (!activeRows.length) return message.warning("Nothing to save");
    if (errors.length) {
      Modal.warning({
        title: `Fix ${isCash ? "cash" : "statement"} bulk rows`,
        width: 620,
        content: (
          <div className="bulk-validation-list">
            {errors.slice(0, 12).map((error) => <div key={error}>{error}</div>)}
            {errors.length > 12 ? <div>And {errors.length - 12} more issue(s).</div> : null}
          </div>
        )
      });
      return;
    }

    const payload = activeRows.map(({ row }) => isCash ? {
      dated: row.dated,
      paidOf: row.paidOf,
      paymentOf: row.paymentOf,
      debit: Number(row.debit ?? 0),
      credit: Number(row.credit ?? 0),
      remarks: row.remarks
    } : {
      tranDate: row.dated,
      chequeNo: row.chequeNo,
      particulars: row.particulars,
      debit: Number(row.debit ?? 0),
      credit: Number(row.credit ?? 0),
      narration: row.narration,
      type: Number(row.debit ?? 0) > 0 ? "Debit" : "Credit"
    });

    setSaving(true);
    try {
      if (draft && isCash) await submitDraft(draft.id, payload);
      else await saveBulk(saveUrl, payload);
      message.success(`${isCash ? "Cash" : "Statement"} entries saved`);
      onSaved();
      onClose();
    } catch {
      message.error(`Unable to save ${isCash ? "cash" : "statement"} entries`);
    } finally {
      setSaving(false);
    }
  };

  const pasteRows = (text: string) => {
    const parsed = text
      .split(/\r?\n/)
      .map((line) => line.trimEnd())
      .filter(Boolean)
      .map((line) => line.split("\t"));
    if (!parsed.length) return;
    const nextRows = parsed.map((cells) => {
      if (isCash) {
        return {
          key: crypto.randomUUID(),
          dated: normalizePastedDate(cells[0]) ?? todayInput(),
          paidOf: cells[1]?.trim(),
          paymentOf: cells[2]?.trim(),
          debit: parsePastedNumber(cells[3]),
          credit: parsePastedNumber(cells[4]),
          remarks: cells[5]?.trim()
        };
      }
      return {
        key: crypto.randomUUID(),
        dated: normalizePastedDate(cells[0]) ?? todayInput(),
        chequeNo: cells[1]?.trim(),
        particulars: cells[2]?.trim(),
        debit: parsePastedNumber(cells[3]),
        credit: parsePastedNumber(cells[4]),
        narration: cells[5]?.trim()
      };
    });
    setRows(nextRows);
    message.success(`${nextRows.length} row(s) pasted`);
  };

  const columns: ColumnsType<SimpleBulkRow> = [
    { title: "#", width: 34, className: "bulk-row-number-cell", render: (_, __, index) => index + 1, fixed: "left" },
    {
      title: "",
      width: 38,
      className: "bulk-row-action-cell",
      fixed: "left",
      render: (_, row) => (
        <Button
          danger
          size="small"
          className="bulk-row-remove"
          icon={<DeleteOutlined />}
          onClick={() => setRows((current) => {
            const next = current.filter((item) => item.key !== row.key);
            return next.length ? next : [createSimpleBulkRow()];
          })}
        />
      )
    },
    {
      title: isCash ? "Entry Date" : "Transaction Date",
      width: 150,
      render: (_, row) => <DatePicker format={UI_DATE_FORMAT} value={dayjs(row.dated)} onChange={(d) => update(row.key, { dated: d?.format("YYYY-MM-DD") ?? todayInput() })} />
    },
    ...(isCash ? [
      {
        title: "Paid Of",
        width: 260,
        render: (_: unknown, row: SimpleBulkRow) => (
          <SmartLookupField lookupKey="party" label={row.paidOf} showSearchButton={false} onChange={(v) => update(row.key, { paidOf: v.label })} />
        )
      },
      {
        title: "Payment Of",
        width: 260,
        render: (_: unknown, row: SimpleBulkRow) => (
          <SmartLookupField lookupKey="bankAccount" label={row.paymentOf} parentContext={{ bankId: "" }} showSearchButton={false} onChange={(v) => update(row.key, { paymentOf: v.label })} />
        )
      }
    ] : [
      {
        title: "Account (Cash/Bank)",
        width: 260,
        render: (_: unknown, row: SimpleBulkRow) => (
          <SmartLookupField lookupKey="bankAccount" label={row.chequeNo} parentContext={{ bankId: "" }} showSearchButton={false} onChange={(v) => update(row.key, { chequeNo: v.label })} />
        )
      },
      {
        title: "Particulars",
        width: 280,
        render: (_: unknown, row: SimpleBulkRow) => (
          <SmartLookupField lookupKey="party" label={row.particulars} showSearchButton={false} onChange={(v) => update(row.key, { particulars: v.label })} />
        )
      }
    ]),
    {
      title: "Debit",
      width: 140,
      align: "right",
      render: (_, row) => <InputNumber min={0} value={row.debit ?? undefined} onChange={(v) => update(row.key, { debit: Number(v ?? 0), ...(Number(v ?? 0) > 0 ? { credit: 0 } : {}) })} style={{ width: "100%" }} />
    },
    {
      title: "Credit",
      width: 140,
      align: "right",
      render: (_, row) => <InputNumber min={0} value={row.credit ?? undefined} onChange={(v) => update(row.key, { credit: Number(v ?? 0), ...(Number(v ?? 0) > 0 ? { debit: 0 } : {}) })} style={{ width: "100%" }} />
    },
    {
      title: isCash ? "Remarks" : "Narration",
      width: 340,
      render: (_, row) => (
        <Input.TextArea
          autoSize={{ minRows: 1, maxRows: 2 }}
          value={isCash ? row.remarks : row.narration}
          onChange={(event) => update(row.key, isCash ? { remarks: event.target.value } : { narration: event.target.value })}
        />
      )
    },
  ];

  return (
    <Modal
      title={title}
      open={open}
      onCancel={onClose}
      width="100vw"
      className="bulk-entry-modal"
      style={{ top: 0, paddingBottom: 0 }}
      footer={
        <div className="bulk-footer">
          <Space>
            <Button onClick={() => setRows((current) => [...current, createSimpleBulkRow()])}>Add Row</Button>
            {isCash ? <Button onClick={saveDraft}>Save Draft</Button> : null}
          </Space>
          <Space>
            <Tag color="red">Debit {formatInr(totals.debit)}</Tag>
            <Tag color="green">Credit {formatInr(totals.credit)}</Tag>
            <Button onClick={onClose}>Close</Button>
            <Button type="primary" icon={<SaveOutlined />} loading={saving} onClick={save}>Save All</Button>
          </Space>
        </div>
      }
    >
      <div
        className="bulk-grid-wrap"
        onPaste={(event) => {
          const keyState = event as unknown as { ctrlKey?: boolean; shiftKey?: boolean };
          if (keyState.ctrlKey === false || keyState.shiftKey === false) return;
          event.preventDefault();
          pasteRows(event.clipboardData.getData("text"));
        }}
      >
        <div className="bulk-helper-strip">
          <Tag color="blue">Ctrl+Shift+V paste</Tag>
          {isCash ? <Tag color="green">Draft enabled</Tag> : null}
          <span>
            {isCash
              ? "Paste order: Date, Paid Of, Payment Of, Debit, Credit, Remarks."
              : "Paste order: Date, Account, Particulars, Debit, Credit, Narration."}
          </span>
        </div>
        <PersistedTable
          tableId={`simple-bulk-entry-${moduleKey}`}
          rowKey="key"
          rowClassName={(row) => invalidRowKeys.has(row.key) ? "bulk-row-invalid" : ""}
          enableColumnSettings={false}
          size="small"
          bordered
          pagination={false}
          scroll={{ x: isCash ? 1420 : 1520 }}
          dataSource={rows}
          columns={columns}
        />
      </div>
    </Modal>
  );
}

function buildSimpleDraftTitle(rows: SimpleBulkRow[], fallback: string) {
  const firstActive = rows.find((row) => row.paidOf || row.paymentOf || row.particulars || row.chequeNo || row.debit || row.credit || row.remarks || row.narration);
  if (!firstActive) return fallback;
  return `${firstActive.dated} - ${firstActive.paidOf || firstActive.particulars || fallback}`;
}
